import { currentStations, type Station } from '../data/stations'
import { LINKS } from '../config/links'
import { SITE, absoluteUrl } from './site'

const logo = {
  '@type': 'ImageObject',
  url: absoluteUrl('/web-app-manifest-512x512.png'),
  width: 512,
  height: 512,
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    alternateName: ['Music24 Jewish Music Radio', 'Music 24', 'Jewish Music Radio'],
    url: SITE.url,
    description: SITE.defaultDescription,
    inLanguage: 'en',
    publisher: { '@id': `${SITE.url}/#organization` },
  }
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'Brand'],
    '@id': `${SITE.url}/#organization`,
    name: SITE.name,
    url: SITE.url,
    logo,
    image: SITE.ogImage,
    email: LINKS.supportEmail,
    sameAs: [
      LINKS.webApp,
      LINKS.androidPlayStore,
      'https://chromewebstore.google.com/detail/phlklaacgpochbampgacgefkdmnepaee',
    ],
  }
}

export function radioServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'RadioBroadcastService',
    '@id': `${SITE.url}/#radio`,
    name: 'Music24 Jewish Music Radio',
    url: SITE.url,
    description: SITE.defaultDescription,
    genre: ['Jewish music', 'Orthodox Jewish music', 'Hasidic music', 'Yiddish music'],
    inLanguage: ['en', 'he', 'yi'],
    broadcastTimezone: 'America/New_York',
    broadcastDisplayName: 'Music24',
    provider: { '@id': `${SITE.url}/#organization` },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  }
}

export function webAppSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Music24',
    url: LINKS.webApp,
    applicationCategory: 'MusicApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description: 'Free Jewish music radio player — live stations in the browser.',
  }
}

export function mobileAppsSchema() {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: 'Music24',
      operatingSystem: 'ANDROID',
      applicationCategory: 'MusicApplication',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      url: LINKS.androidPlayStore,
      installUrl: LINKS.androidPlayStore,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: 'Music24',
      operatingSystem: 'IOS',
      applicationCategory: 'MusicApplication',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      url: LINKS.iosAppStore,
      installUrl: LINKS.iosAppStore,
    },
  ]
}

export function breadcrumbSchema(
  items: Array<{ name: string; path: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}

export function stationSchema(station: Station) {
  const url = absoluteUrl(`/stations/${station.slug}`)
  return {
    '@context': 'https://schema.org',
    '@type': 'RadioStation',
    '@id': `${url}#station`,
    name: `${station.name} Jewish Music Radio`,
    alternateName: `${station.name} — Music24`,
    description: station.description,
    url,
    image: absoluteUrl(station.icon),
    genre: ['Jewish music', ...station.keywords.slice(0, 4)],
    inLanguage: ['en', 'he', 'yi'],
    broadcastDisplayName: station.name,
    parentOrganization: { '@id': `${SITE.url}/#organization` },
    isPartOf: { '@id': `${SITE.url}/#radio` },
    potentialAction: {
      '@type': 'ListenAction',
      target: `${LINKS.webApp}/?station=${encodeURIComponent(station.streamId)}`,
    },
  }
}

export function faqSchema(items: Array<{ q: string; a: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }
}

export function stationsItemListSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Music24 Jewish music radio stations',
    numberOfItems: currentStations().length,
    itemListElement: currentStations().map((station, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: absoluteUrl(`/stations/${station.slug}`),
      name: `${station.name} Jewish music radio`,
    })),
  }
}
